/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Tessera Backend API
 * Tessera backend application with Bearer token authentication and multi-tenant support
 */
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  InvalidateOptions,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { BodyType, ErrorType } from '../api-instance';
import { apiInstance } from '../api-instance';
import type {
  ControlPlaneGetCatalogPathParameters,
  CreateReplicationGroupPathParameters,
  DeleteCatalogPathParameters,
  DeleteReplicationGroupPathParameters,
  GetLakePathParameters,
  GetReplicationGroupPathParameters,
  GetReplicationRunByIdPathParameters,
  GetReplicationRunPathParameters,
  ListCatalogsParams,
  ListCatalogsPathParameters,
  ListReplicationGroupsParams,
  ListReplicationGroupsPathParameters,
  ListReplicationRunsParams,
  ListReplicationRunsPathParameters,
  ListReplicationTableRunsParams,
  ListReplicationTableRunsPathParameters,
  PutCatalogPathParameters,
  PutLakePathParameters,
  StartReplicationRunPathParameters,
  TLCatalogPageResponse,
  TLCreateReplicationGroupRequest,
  TLDataFoundryControlPlaneCatalogResponse,
  TLHTTPValidationError,
  TLLakeResponse,
  TLListSourcesResponse,
  TLProvisionSourceRequest,
  TLProvisionSourceResponse,
  TLPutCatalogRequest,
  TLReplicationGroupPageResponse,
  TLReplicationGroupResponse,
  TLReplicationGroupRunPageResponse,
  TLReplicationGroupRunResponse,
  TLReplicationTableRunPageResponse,
  TLStartReplicationRunRequest,
  TLStartReplicationRunResponse,
  TLUpdateSourceRequest,
  TLUpdateSourceResponse,
  UpdateReplicationGroupPathParameters,
  UpdateSourcePathParameters,
} from './tessera-backend-api.schemas';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * @summary Get Lake
 */
export const getLake = (
  { lake }: GetLakePathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLLakeResponse>({ url: `/foundry/control-plane/lakes/${lake}`, method: 'GET', signal }, options);
};

export const getGetLakeQueryKey = ({ lake }: GetLakePathParameters) => {
  return ['foundry', 'control-plane', 'lakes', lake] as const;
};

export const getGetLakeQueryOptions = <
  TData = Awaited<ReturnType<typeof getLake>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { lake }: GetLakePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getLake>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetLakeQueryKey({ lake });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getLake>>> = ({ signal }) =>
    getLake({ lake }, requestOptions, signal);

  return { queryKey, queryFn, enabled: lake !== null && lake !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getLake>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetLakeQueryResult = NonNullable<Awaited<ReturnType<typeof getLake>>>;
export type GetLakeQueryError = ErrorType<TLHTTPValidationError>;

export function useGetLake<TData = Awaited<ReturnType<typeof getLake>>, TError = ErrorType<TLHTTPValidationError>>(
  pathParams: GetLakePathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getLake>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<Awaited<ReturnType<typeof getLake>>, TError, Awaited<ReturnType<typeof getLake>>>,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetLake<TData = Awaited<ReturnType<typeof getLake>>, TError = ErrorType<TLHTTPValidationError>>(
  pathParams: GetLakePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getLake>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<Awaited<ReturnType<typeof getLake>>, TError, Awaited<ReturnType<typeof getLake>>>,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetLake<TData = Awaited<ReturnType<typeof getLake>>, TError = ErrorType<TLHTTPValidationError>>(
  pathParams: GetLakePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getLake>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Lake
 */

export function useGetLake<TData = Awaited<ReturnType<typeof getLake>>, TError = ErrorType<TLHTTPValidationError>>(
  { lake }: GetLakePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getLake>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetLakeQueryOptions({ lake }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Lake
 */
export const prefetchGetLakeQuery = async <
  TData = Awaited<ReturnType<typeof getLake>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { lake }: GetLakePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getLake>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetLakeQueryOptions({ lake }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Lake
 */
export const invalidateGetLake = async (
  queryClient: QueryClient,
  { lake }: GetLakePathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetLakeQueryKey({ lake }) }, options);

  return queryClient;
};

/**
 * @summary Put Lake
 */
export const putLake = (
  { lake }: PutLakePathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLLakeResponse>({ url: `/foundry/control-plane/lakes/${lake}`, method: 'PUT', signal }, options);
};

export const getPutLakeQueryKey = ({ lake }: PutLakePathParameters) => {
  return ['PUT', 'foundry', 'control-plane', 'lakes', lake] as const;
};

export const getPutLakeQueryOptions = <
  TData = Awaited<ReturnType<typeof putLake>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { lake }: PutLakePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof putLake>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getPutLakeQueryKey({ lake });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof putLake>>> = ({ signal }) =>
    putLake({ lake }, requestOptions, signal);

  return { queryKey, queryFn, enabled: lake !== null && lake !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof putLake>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type PutLakeQueryResult = NonNullable<Awaited<ReturnType<typeof putLake>>>;
export type PutLakeQueryError = ErrorType<TLHTTPValidationError>;

export function usePutLake<TData = Awaited<ReturnType<typeof putLake>>, TError = ErrorType<TLHTTPValidationError>>(
  pathParams: PutLakePathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof putLake>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<Awaited<ReturnType<typeof putLake>>, TError, Awaited<ReturnType<typeof putLake>>>,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePutLake<TData = Awaited<ReturnType<typeof putLake>>, TError = ErrorType<TLHTTPValidationError>>(
  pathParams: PutLakePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof putLake>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<Awaited<ReturnType<typeof putLake>>, TError, Awaited<ReturnType<typeof putLake>>>,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePutLake<TData = Awaited<ReturnType<typeof putLake>>, TError = ErrorType<TLHTTPValidationError>>(
  pathParams: PutLakePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof putLake>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Put Lake
 */

export function usePutLake<TData = Awaited<ReturnType<typeof putLake>>, TError = ErrorType<TLHTTPValidationError>>(
  { lake }: PutLakePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof putLake>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getPutLakeQueryOptions({ lake }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Put Lake
 */
export const prefetchPutLakeQuery = async <
  TData = Awaited<ReturnType<typeof putLake>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { lake }: PutLakePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof putLake>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getPutLakeQueryOptions({ lake }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Put Lake
 */
export const invalidatePutLake = async (
  queryClient: QueryClient,
  { lake }: PutLakePathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getPutLakeQueryKey({ lake }) }, options);

  return queryClient;
};

/**
 * @summary List Catalogs
 */
export const listCatalogs = (
  { lake }: ListCatalogsPathParameters,
  params?: ListCatalogsParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLCatalogPageResponse>(
    { url: `/foundry/control-plane/lakes/${lake}/catalogs`, method: 'GET', params, signal },
    options,
  );
};

export const getListCatalogsQueryKey = ({ lake }: ListCatalogsPathParameters, params?: ListCatalogsParams) => {
  return ['foundry', 'control-plane', 'lakes', lake, 'catalogs', ...(params ? [params] : [])] as const;
};

export const getListCatalogsQueryOptions = <
  TData = Awaited<ReturnType<typeof listCatalogs>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { lake }: ListCatalogsPathParameters,
  params?: ListCatalogsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listCatalogs>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListCatalogsQueryKey({ lake }, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listCatalogs>>> = ({ signal }) =>
    listCatalogs({ lake }, params, requestOptions, signal);

  return { queryKey, queryFn, enabled: lake !== null && lake !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listCatalogs>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListCatalogsQueryResult = NonNullable<Awaited<ReturnType<typeof listCatalogs>>>;
export type ListCatalogsQueryError = ErrorType<TLHTTPValidationError>;

export function useListCatalogs<
  TData = Awaited<ReturnType<typeof listCatalogs>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ListCatalogsPathParameters,
  params: undefined | ListCatalogsParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof listCatalogs>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listCatalogs>>,
          TError,
          Awaited<ReturnType<typeof listCatalogs>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListCatalogs<
  TData = Awaited<ReturnType<typeof listCatalogs>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ListCatalogsPathParameters,
  params?: ListCatalogsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listCatalogs>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listCatalogs>>,
          TError,
          Awaited<ReturnType<typeof listCatalogs>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListCatalogs<
  TData = Awaited<ReturnType<typeof listCatalogs>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ListCatalogsPathParameters,
  params?: ListCatalogsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listCatalogs>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List Catalogs
 */

export function useListCatalogs<
  TData = Awaited<ReturnType<typeof listCatalogs>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { lake }: ListCatalogsPathParameters,
  params?: ListCatalogsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listCatalogs>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListCatalogsQueryOptions({ lake }, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary List Catalogs
 */
export const prefetchListCatalogsQuery = async <
  TData = Awaited<ReturnType<typeof listCatalogs>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { lake }: ListCatalogsPathParameters,
  params?: ListCatalogsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listCatalogs>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getListCatalogsQueryOptions({ lake }, params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary List Catalogs
 */
export const invalidateListCatalogs = async (
  queryClient: QueryClient,
  { lake }: ListCatalogsPathParameters,
  params?: ListCatalogsParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getListCatalogsQueryKey({ lake }, params) }, options);

  return queryClient;
};

/**
 * @summary Delete Catalog
 */
export const deleteCatalog = (
  { lake, catalog }: DeleteCatalogPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<void>(
    { url: `/foundry/control-plane/lakes/${lake}/catalogs/${catalog}`, method: 'DELETE', signal },
    options,
  );
};

export const getDeleteCatalogQueryKey = ({ lake, catalog }: DeleteCatalogPathParameters) => {
  return ['DELETE', 'foundry', 'control-plane', 'lakes', lake, 'catalogs', catalog] as const;
};

export const getDeleteCatalogQueryOptions = <
  TData = Awaited<ReturnType<typeof deleteCatalog>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { lake, catalog }: DeleteCatalogPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteCatalog>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getDeleteCatalogQueryKey({ lake, catalog });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof deleteCatalog>>> = ({ signal }) =>
    deleteCatalog({ lake, catalog }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: lake !== null && lake !== undefined && catalog !== null && catalog !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof deleteCatalog>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type DeleteCatalogQueryResult = NonNullable<Awaited<ReturnType<typeof deleteCatalog>>>;
export type DeleteCatalogQueryError = ErrorType<TLHTTPValidationError>;

export function useDeleteCatalog<
  TData = Awaited<ReturnType<typeof deleteCatalog>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteCatalogPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteCatalog>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof deleteCatalog>>,
          TError,
          Awaited<ReturnType<typeof deleteCatalog>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDeleteCatalog<
  TData = Awaited<ReturnType<typeof deleteCatalog>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteCatalogPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteCatalog>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof deleteCatalog>>,
          TError,
          Awaited<ReturnType<typeof deleteCatalog>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDeleteCatalog<
  TData = Awaited<ReturnType<typeof deleteCatalog>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteCatalogPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteCatalog>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Delete Catalog
 */

export function useDeleteCatalog<
  TData = Awaited<ReturnType<typeof deleteCatalog>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { lake, catalog }: DeleteCatalogPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteCatalog>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getDeleteCatalogQueryOptions({ lake, catalog }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Delete Catalog
 */
export const prefetchDeleteCatalogQuery = async <
  TData = Awaited<ReturnType<typeof deleteCatalog>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { lake, catalog }: DeleteCatalogPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteCatalog>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getDeleteCatalogQueryOptions({ lake, catalog }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Delete Catalog
 */
export const invalidateDeleteCatalog = async (
  queryClient: QueryClient,
  { lake, catalog }: DeleteCatalogPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getDeleteCatalogQueryKey({ lake, catalog }) }, options);

  return queryClient;
};

/**
 * @summary Get Catalog
 */
export const controlPlaneGetCatalog = (
  { lake, catalog }: ControlPlaneGetCatalogPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLDataFoundryControlPlaneCatalogResponse>(
    { url: `/foundry/control-plane/lakes/${lake}/catalogs/${catalog}`, method: 'GET', signal },
    options,
  );
};

export const getControlPlaneGetCatalogQueryKey = ({ lake, catalog }: ControlPlaneGetCatalogPathParameters) => {
  return ['foundry', 'control-plane', 'lakes', lake, 'catalogs', catalog] as const;
};

export const getControlPlaneGetCatalogQueryOptions = <
  TData = Awaited<ReturnType<typeof controlPlaneGetCatalog>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { lake, catalog }: ControlPlaneGetCatalogPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof controlPlaneGetCatalog>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getControlPlaneGetCatalogQueryKey({ lake, catalog });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof controlPlaneGetCatalog>>> = ({ signal }) =>
    controlPlaneGetCatalog({ lake, catalog }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: lake !== null && lake !== undefined && catalog !== null && catalog !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof controlPlaneGetCatalog>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type ControlPlaneGetCatalogQueryResult = NonNullable<Awaited<ReturnType<typeof controlPlaneGetCatalog>>>;
export type ControlPlaneGetCatalogQueryError = ErrorType<TLHTTPValidationError>;

export function useControlPlaneGetCatalog<
  TData = Awaited<ReturnType<typeof controlPlaneGetCatalog>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ControlPlaneGetCatalogPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof controlPlaneGetCatalog>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof controlPlaneGetCatalog>>,
          TError,
          Awaited<ReturnType<typeof controlPlaneGetCatalog>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useControlPlaneGetCatalog<
  TData = Awaited<ReturnType<typeof controlPlaneGetCatalog>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ControlPlaneGetCatalogPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof controlPlaneGetCatalog>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof controlPlaneGetCatalog>>,
          TError,
          Awaited<ReturnType<typeof controlPlaneGetCatalog>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useControlPlaneGetCatalog<
  TData = Awaited<ReturnType<typeof controlPlaneGetCatalog>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ControlPlaneGetCatalogPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof controlPlaneGetCatalog>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Catalog
 */

export function useControlPlaneGetCatalog<
  TData = Awaited<ReturnType<typeof controlPlaneGetCatalog>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { lake, catalog }: ControlPlaneGetCatalogPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof controlPlaneGetCatalog>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getControlPlaneGetCatalogQueryOptions({ lake, catalog }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Catalog
 */
export const prefetchControlPlaneGetCatalogQuery = async <
  TData = Awaited<ReturnType<typeof controlPlaneGetCatalog>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { lake, catalog }: ControlPlaneGetCatalogPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof controlPlaneGetCatalog>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getControlPlaneGetCatalogQueryOptions({ lake, catalog }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Catalog
 */
export const invalidateControlPlaneGetCatalog = async (
  queryClient: QueryClient,
  { lake, catalog }: ControlPlaneGetCatalogPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getControlPlaneGetCatalogQueryKey({ lake, catalog }) }, options);

  return queryClient;
};

/**
 * @summary Put Catalog
 */
export const putCatalog = (
  { lake, catalog }: PutCatalogPathParameters,
  tLPutCatalogRequestNull?: BodyType<TLPutCatalogRequest | null> | null,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLDataFoundryControlPlaneCatalogResponse>(
    {
      url: `/foundry/control-plane/lakes/${lake}/catalogs/${catalog}`,
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      data: tLPutCatalogRequestNull,
      signal,
    },
    options,
  );
};

export const getPutCatalogQueryKey = (
  { lake, catalog }: PutCatalogPathParameters,
  tLPutCatalogRequestNull?: BodyType<TLPutCatalogRequest | null>,
) => {
  return ['PUT', 'foundry', 'control-plane', 'lakes', lake, 'catalogs', catalog, tLPutCatalogRequestNull] as const;
};

export const getPutCatalogQueryOptions = <
  TData = Awaited<ReturnType<typeof putCatalog>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { lake, catalog }: PutCatalogPathParameters,
  tLPutCatalogRequestNull?: BodyType<TLPutCatalogRequest | null>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof putCatalog>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getPutCatalogQueryKey({ lake, catalog }, tLPutCatalogRequestNull);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof putCatalog>>> = ({ signal }) =>
    putCatalog({ lake, catalog }, tLPutCatalogRequestNull, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: lake !== null && lake !== undefined && catalog !== null && catalog !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof putCatalog>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type PutCatalogQueryResult = NonNullable<Awaited<ReturnType<typeof putCatalog>>>;
export type PutCatalogQueryError = ErrorType<TLHTTPValidationError>;

export function usePutCatalog<
  TData = Awaited<ReturnType<typeof putCatalog>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: PutCatalogPathParameters,
  tLPutCatalogRequestNull: undefined | BodyType<TLPutCatalogRequest | null>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof putCatalog>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof putCatalog>>,
          TError,
          Awaited<ReturnType<typeof putCatalog>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePutCatalog<
  TData = Awaited<ReturnType<typeof putCatalog>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: PutCatalogPathParameters,
  tLPutCatalogRequestNull?: BodyType<TLPutCatalogRequest | null>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof putCatalog>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof putCatalog>>,
          TError,
          Awaited<ReturnType<typeof putCatalog>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function usePutCatalog<
  TData = Awaited<ReturnType<typeof putCatalog>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: PutCatalogPathParameters,
  tLPutCatalogRequestNull?: BodyType<TLPutCatalogRequest | null>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof putCatalog>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Put Catalog
 */

export function usePutCatalog<
  TData = Awaited<ReturnType<typeof putCatalog>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { lake, catalog }: PutCatalogPathParameters,
  tLPutCatalogRequestNull?: BodyType<TLPutCatalogRequest | null>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof putCatalog>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getPutCatalogQueryOptions({ lake, catalog }, tLPutCatalogRequestNull, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Put Catalog
 */
export const prefetchPutCatalogQuery = async <
  TData = Awaited<ReturnType<typeof putCatalog>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { lake, catalog }: PutCatalogPathParameters,
  tLPutCatalogRequestNull?: BodyType<TLPutCatalogRequest | null>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof putCatalog>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getPutCatalogQueryOptions({ lake, catalog }, tLPutCatalogRequestNull, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Put Catalog
 */
export const invalidatePutCatalog = async (
  queryClient: QueryClient,
  { lake, catalog }: PutCatalogPathParameters,
  tLPutCatalogRequestNull?: BodyType<TLPutCatalogRequest | null>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getPutCatalogQueryKey({ lake, catalog }, tLPutCatalogRequestNull) },
    options,
  );

  return queryClient;
};

/**
 * @summary List Replication Groups
 */
export const listReplicationGroups = (
  { lake, catalog }: ListReplicationGroupsPathParameters,
  params?: ListReplicationGroupsParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLReplicationGroupPageResponse>(
    {
      url: `/foundry/control-plane/lakes/${lake}/catalogs/${catalog}/replication-groups`,
      method: 'GET',
      params,
      signal,
    },
    options,
  );
};

export const getListReplicationGroupsQueryKey = (
  { lake, catalog }: ListReplicationGroupsPathParameters,
  params?: ListReplicationGroupsParams,
) => {
  return [
    'foundry',
    'control-plane',
    'lakes',
    lake,
    'catalogs',
    catalog,
    'replication-groups',
    ...(params ? [params] : []),
  ] as const;
};

export const getListReplicationGroupsQueryOptions = <
  TData = Awaited<ReturnType<typeof listReplicationGroups>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { lake, catalog }: ListReplicationGroupsPathParameters,
  params?: ListReplicationGroupsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listReplicationGroups>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListReplicationGroupsQueryKey({ lake, catalog }, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listReplicationGroups>>> = ({ signal }) =>
    listReplicationGroups({ lake, catalog }, params, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: lake !== null && lake !== undefined && catalog !== null && catalog !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof listReplicationGroups>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type ListReplicationGroupsQueryResult = NonNullable<Awaited<ReturnType<typeof listReplicationGroups>>>;
export type ListReplicationGroupsQueryError = ErrorType<TLHTTPValidationError>;

export function useListReplicationGroups<
  TData = Awaited<ReturnType<typeof listReplicationGroups>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ListReplicationGroupsPathParameters,
  params: undefined | ListReplicationGroupsParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof listReplicationGroups>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listReplicationGroups>>,
          TError,
          Awaited<ReturnType<typeof listReplicationGroups>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListReplicationGroups<
  TData = Awaited<ReturnType<typeof listReplicationGroups>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ListReplicationGroupsPathParameters,
  params?: ListReplicationGroupsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listReplicationGroups>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listReplicationGroups>>,
          TError,
          Awaited<ReturnType<typeof listReplicationGroups>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListReplicationGroups<
  TData = Awaited<ReturnType<typeof listReplicationGroups>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ListReplicationGroupsPathParameters,
  params?: ListReplicationGroupsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listReplicationGroups>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List Replication Groups
 */

export function useListReplicationGroups<
  TData = Awaited<ReturnType<typeof listReplicationGroups>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { lake, catalog }: ListReplicationGroupsPathParameters,
  params?: ListReplicationGroupsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listReplicationGroups>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListReplicationGroupsQueryOptions({ lake, catalog }, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary List Replication Groups
 */
export const prefetchListReplicationGroupsQuery = async <
  TData = Awaited<ReturnType<typeof listReplicationGroups>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { lake, catalog }: ListReplicationGroupsPathParameters,
  params?: ListReplicationGroupsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listReplicationGroups>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getListReplicationGroupsQueryOptions({ lake, catalog }, params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary List Replication Groups
 */
export const invalidateListReplicationGroups = async (
  queryClient: QueryClient,
  { lake, catalog }: ListReplicationGroupsPathParameters,
  params?: ListReplicationGroupsParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getListReplicationGroupsQueryKey({ lake, catalog }, params) },
    options,
  );

  return queryClient;
};

/**
 * @summary Create Replication Group
 */
export const createReplicationGroup = (
  { lake, catalog }: CreateReplicationGroupPathParameters,
  tLCreateReplicationGroupRequest: BodyType<TLCreateReplicationGroupRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLReplicationGroupResponse>(
    {
      url: `/foundry/control-plane/lakes/${lake}/catalogs/${catalog}/replication-groups`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLCreateReplicationGroupRequest,
      signal,
    },
    options,
  );
};

export const getCreateReplicationGroupQueryKey = (
  { lake, catalog }: CreateReplicationGroupPathParameters,
  tLCreateReplicationGroupRequest?: BodyType<TLCreateReplicationGroupRequest>,
) => {
  return [
    'POST',
    'foundry',
    'control-plane',
    'lakes',
    lake,
    'catalogs',
    catalog,
    'replication-groups',
    tLCreateReplicationGroupRequest,
  ] as const;
};

export const getCreateReplicationGroupQueryOptions = <
  TData = Awaited<ReturnType<typeof createReplicationGroup>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { lake, catalog }: CreateReplicationGroupPathParameters,
  tLCreateReplicationGroupRequest: BodyType<TLCreateReplicationGroupRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createReplicationGroup>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getCreateReplicationGroupQueryKey({ lake, catalog }, tLCreateReplicationGroupRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof createReplicationGroup>>> = ({ signal }) =>
    createReplicationGroup({ lake, catalog }, tLCreateReplicationGroupRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: lake !== null && lake !== undefined && catalog !== null && catalog !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof createReplicationGroup>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type CreateReplicationGroupQueryResult = NonNullable<Awaited<ReturnType<typeof createReplicationGroup>>>;
export type CreateReplicationGroupQueryError = ErrorType<TLHTTPValidationError>;

export function useCreateReplicationGroup<
  TData = Awaited<ReturnType<typeof createReplicationGroup>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: CreateReplicationGroupPathParameters,
  tLCreateReplicationGroupRequest: BodyType<TLCreateReplicationGroupRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof createReplicationGroup>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof createReplicationGroup>>,
          TError,
          Awaited<ReturnType<typeof createReplicationGroup>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateReplicationGroup<
  TData = Awaited<ReturnType<typeof createReplicationGroup>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: CreateReplicationGroupPathParameters,
  tLCreateReplicationGroupRequest: BodyType<TLCreateReplicationGroupRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createReplicationGroup>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof createReplicationGroup>>,
          TError,
          Awaited<ReturnType<typeof createReplicationGroup>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateReplicationGroup<
  TData = Awaited<ReturnType<typeof createReplicationGroup>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: CreateReplicationGroupPathParameters,
  tLCreateReplicationGroupRequest: BodyType<TLCreateReplicationGroupRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createReplicationGroup>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Create Replication Group
 */

export function useCreateReplicationGroup<
  TData = Awaited<ReturnType<typeof createReplicationGroup>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { lake, catalog }: CreateReplicationGroupPathParameters,
  tLCreateReplicationGroupRequest: BodyType<TLCreateReplicationGroupRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createReplicationGroup>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getCreateReplicationGroupQueryOptions(
    { lake, catalog },
    tLCreateReplicationGroupRequest,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Create Replication Group
 */
export const prefetchCreateReplicationGroupQuery = async <
  TData = Awaited<ReturnType<typeof createReplicationGroup>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { lake, catalog }: CreateReplicationGroupPathParameters,
  tLCreateReplicationGroupRequest: BodyType<TLCreateReplicationGroupRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createReplicationGroup>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getCreateReplicationGroupQueryOptions(
    { lake, catalog },
    tLCreateReplicationGroupRequest,
    options,
  );

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Create Replication Group
 */
export const invalidateCreateReplicationGroup = async (
  queryClient: QueryClient,
  { lake, catalog }: CreateReplicationGroupPathParameters,
  tLCreateReplicationGroupRequest: BodyType<TLCreateReplicationGroupRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getCreateReplicationGroupQueryKey({ lake, catalog }, tLCreateReplicationGroupRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Delete Replication Group
 */
export const deleteReplicationGroup = (
  { lake, catalog, replicationGroupId }: DeleteReplicationGroupPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<void>(
    {
      url: `/foundry/control-plane/lakes/${lake}/catalogs/${catalog}/replication-groups/${replicationGroupId}`,
      method: 'DELETE',
      signal,
    },
    options,
  );
};

export const getDeleteReplicationGroupQueryKey = ({
  lake,
  catalog,
  replicationGroupId,
}: DeleteReplicationGroupPathParameters) => {
  return [
    'DELETE',
    'foundry',
    'control-plane',
    'lakes',
    lake,
    'catalogs',
    catalog,
    'replication-groups',
    replicationGroupId,
  ] as const;
};

export const getDeleteReplicationGroupQueryOptions = <
  TData = Awaited<ReturnType<typeof deleteReplicationGroup>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { lake, catalog, replicationGroupId }: DeleteReplicationGroupPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteReplicationGroup>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getDeleteReplicationGroupQueryKey({ lake, catalog, replicationGroupId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof deleteReplicationGroup>>> = ({ signal }) =>
    deleteReplicationGroup({ lake, catalog, replicationGroupId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled:
      lake !== null &&
      lake !== undefined &&
      catalog !== null &&
      catalog !== undefined &&
      replicationGroupId !== null &&
      replicationGroupId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof deleteReplicationGroup>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type DeleteReplicationGroupQueryResult = NonNullable<Awaited<ReturnType<typeof deleteReplicationGroup>>>;
export type DeleteReplicationGroupQueryError = ErrorType<TLHTTPValidationError>;

export function useDeleteReplicationGroup<
  TData = Awaited<ReturnType<typeof deleteReplicationGroup>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteReplicationGroupPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteReplicationGroup>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof deleteReplicationGroup>>,
          TError,
          Awaited<ReturnType<typeof deleteReplicationGroup>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDeleteReplicationGroup<
  TData = Awaited<ReturnType<typeof deleteReplicationGroup>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteReplicationGroupPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteReplicationGroup>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof deleteReplicationGroup>>,
          TError,
          Awaited<ReturnType<typeof deleteReplicationGroup>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDeleteReplicationGroup<
  TData = Awaited<ReturnType<typeof deleteReplicationGroup>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteReplicationGroupPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteReplicationGroup>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Delete Replication Group
 */

export function useDeleteReplicationGroup<
  TData = Awaited<ReturnType<typeof deleteReplicationGroup>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { lake, catalog, replicationGroupId }: DeleteReplicationGroupPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteReplicationGroup>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getDeleteReplicationGroupQueryOptions({ lake, catalog, replicationGroupId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Delete Replication Group
 */
export const prefetchDeleteReplicationGroupQuery = async <
  TData = Awaited<ReturnType<typeof deleteReplicationGroup>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { lake, catalog, replicationGroupId }: DeleteReplicationGroupPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteReplicationGroup>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getDeleteReplicationGroupQueryOptions({ lake, catalog, replicationGroupId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Delete Replication Group
 */
export const invalidateDeleteReplicationGroup = async (
  queryClient: QueryClient,
  { lake, catalog, replicationGroupId }: DeleteReplicationGroupPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getDeleteReplicationGroupQueryKey({ lake, catalog, replicationGroupId }) },
    options,
  );

  return queryClient;
};

/**
 * @summary Get Replication Group
 */
export const getReplicationGroup = (
  { lake, catalog, replicationGroupId }: GetReplicationGroupPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLReplicationGroupResponse>(
    {
      url: `/foundry/control-plane/lakes/${lake}/catalogs/${catalog}/replication-groups/${replicationGroupId}`,
      method: 'GET',
      signal,
    },
    options,
  );
};

export const getGetReplicationGroupQueryKey = ({
  lake,
  catalog,
  replicationGroupId,
}: GetReplicationGroupPathParameters) => {
  return [
    'foundry',
    'control-plane',
    'lakes',
    lake,
    'catalogs',
    catalog,
    'replication-groups',
    replicationGroupId,
  ] as const;
};

export const getGetReplicationGroupQueryOptions = <
  TData = Awaited<ReturnType<typeof getReplicationGroup>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { lake, catalog, replicationGroupId }: GetReplicationGroupPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getReplicationGroup>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetReplicationGroupQueryKey({ lake, catalog, replicationGroupId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getReplicationGroup>>> = ({ signal }) =>
    getReplicationGroup({ lake, catalog, replicationGroupId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled:
      lake !== null &&
      lake !== undefined &&
      catalog !== null &&
      catalog !== undefined &&
      replicationGroupId !== null &&
      replicationGroupId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getReplicationGroup>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetReplicationGroupQueryResult = NonNullable<Awaited<ReturnType<typeof getReplicationGroup>>>;
export type GetReplicationGroupQueryError = ErrorType<TLHTTPValidationError>;

export function useGetReplicationGroup<
  TData = Awaited<ReturnType<typeof getReplicationGroup>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetReplicationGroupPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getReplicationGroup>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getReplicationGroup>>,
          TError,
          Awaited<ReturnType<typeof getReplicationGroup>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetReplicationGroup<
  TData = Awaited<ReturnType<typeof getReplicationGroup>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetReplicationGroupPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getReplicationGroup>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getReplicationGroup>>,
          TError,
          Awaited<ReturnType<typeof getReplicationGroup>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetReplicationGroup<
  TData = Awaited<ReturnType<typeof getReplicationGroup>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetReplicationGroupPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getReplicationGroup>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Replication Group
 */

export function useGetReplicationGroup<
  TData = Awaited<ReturnType<typeof getReplicationGroup>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { lake, catalog, replicationGroupId }: GetReplicationGroupPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getReplicationGroup>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetReplicationGroupQueryOptions({ lake, catalog, replicationGroupId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Replication Group
 */
export const prefetchGetReplicationGroupQuery = async <
  TData = Awaited<ReturnType<typeof getReplicationGroup>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { lake, catalog, replicationGroupId }: GetReplicationGroupPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getReplicationGroup>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetReplicationGroupQueryOptions({ lake, catalog, replicationGroupId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Replication Group
 */
export const invalidateGetReplicationGroup = async (
  queryClient: QueryClient,
  { lake, catalog, replicationGroupId }: GetReplicationGroupPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getGetReplicationGroupQueryKey({ lake, catalog, replicationGroupId }) },
    options,
  );

  return queryClient;
};

/**
 * @summary Update Replication Group
 */
export const updateReplicationGroup = (
  { lake, catalog, replicationGroupId }: UpdateReplicationGroupPathParameters,
  tLCreateReplicationGroupRequest: BodyType<TLCreateReplicationGroupRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLReplicationGroupResponse>(
    {
      url: `/foundry/control-plane/lakes/${lake}/catalogs/${catalog}/replication-groups/${replicationGroupId}`,
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      data: tLCreateReplicationGroupRequest,
      signal,
    },
    options,
  );
};

export const getUpdateReplicationGroupQueryKey = (
  { lake, catalog, replicationGroupId }: UpdateReplicationGroupPathParameters,
  tLCreateReplicationGroupRequest?: BodyType<TLCreateReplicationGroupRequest>,
) => {
  return [
    'PUT',
    'foundry',
    'control-plane',
    'lakes',
    lake,
    'catalogs',
    catalog,
    'replication-groups',
    replicationGroupId,
    tLCreateReplicationGroupRequest,
  ] as const;
};

export const getUpdateReplicationGroupQueryOptions = <
  TData = Awaited<ReturnType<typeof updateReplicationGroup>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { lake, catalog, replicationGroupId }: UpdateReplicationGroupPathParameters,
  tLCreateReplicationGroupRequest: BodyType<TLCreateReplicationGroupRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateReplicationGroup>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getUpdateReplicationGroupQueryKey({ lake, catalog, replicationGroupId }, tLCreateReplicationGroupRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof updateReplicationGroup>>> = ({ signal }) =>
    updateReplicationGroup(
      { lake, catalog, replicationGroupId },
      tLCreateReplicationGroupRequest,
      requestOptions,
      signal,
    );

  return {
    queryKey,
    queryFn,
    enabled:
      lake !== null &&
      lake !== undefined &&
      catalog !== null &&
      catalog !== undefined &&
      replicationGroupId !== null &&
      replicationGroupId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof updateReplicationGroup>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type UpdateReplicationGroupQueryResult = NonNullable<Awaited<ReturnType<typeof updateReplicationGroup>>>;
export type UpdateReplicationGroupQueryError = ErrorType<TLHTTPValidationError>;

export function useUpdateReplicationGroup<
  TData = Awaited<ReturnType<typeof updateReplicationGroup>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateReplicationGroupPathParameters,
  tLCreateReplicationGroupRequest: BodyType<TLCreateReplicationGroupRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateReplicationGroup>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof updateReplicationGroup>>,
          TError,
          Awaited<ReturnType<typeof updateReplicationGroup>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateReplicationGroup<
  TData = Awaited<ReturnType<typeof updateReplicationGroup>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateReplicationGroupPathParameters,
  tLCreateReplicationGroupRequest: BodyType<TLCreateReplicationGroupRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateReplicationGroup>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof updateReplicationGroup>>,
          TError,
          Awaited<ReturnType<typeof updateReplicationGroup>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateReplicationGroup<
  TData = Awaited<ReturnType<typeof updateReplicationGroup>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateReplicationGroupPathParameters,
  tLCreateReplicationGroupRequest: BodyType<TLCreateReplicationGroupRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateReplicationGroup>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Update Replication Group
 */

export function useUpdateReplicationGroup<
  TData = Awaited<ReturnType<typeof updateReplicationGroup>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { lake, catalog, replicationGroupId }: UpdateReplicationGroupPathParameters,
  tLCreateReplicationGroupRequest: BodyType<TLCreateReplicationGroupRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateReplicationGroup>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getUpdateReplicationGroupQueryOptions(
    { lake, catalog, replicationGroupId },
    tLCreateReplicationGroupRequest,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Update Replication Group
 */
export const prefetchUpdateReplicationGroupQuery = async <
  TData = Awaited<ReturnType<typeof updateReplicationGroup>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { lake, catalog, replicationGroupId }: UpdateReplicationGroupPathParameters,
  tLCreateReplicationGroupRequest: BodyType<TLCreateReplicationGroupRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateReplicationGroup>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getUpdateReplicationGroupQueryOptions(
    { lake, catalog, replicationGroupId },
    tLCreateReplicationGroupRequest,
    options,
  );

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Update Replication Group
 */
export const invalidateUpdateReplicationGroup = async (
  queryClient: QueryClient,
  { lake, catalog, replicationGroupId }: UpdateReplicationGroupPathParameters,
  tLCreateReplicationGroupRequest: BodyType<TLCreateReplicationGroupRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    {
      queryKey: getUpdateReplicationGroupQueryKey(
        { lake, catalog, replicationGroupId },
        tLCreateReplicationGroupRequest,
      ),
    },
    options,
  );

  return queryClient;
};

/**
 * @summary List Replication Runs
 */
export const listReplicationRuns = (
  { lake, catalog, replicationGroupId }: ListReplicationRunsPathParameters,
  params?: ListReplicationRunsParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLReplicationGroupRunPageResponse>(
    {
      url: `/foundry/control-plane/lakes/${lake}/catalogs/${catalog}/replication-groups/${replicationGroupId}/runs`,
      method: 'GET',
      params,
      signal,
    },
    options,
  );
};

export const getListReplicationRunsQueryKey = (
  { lake, catalog, replicationGroupId }: ListReplicationRunsPathParameters,
  params?: ListReplicationRunsParams,
) => {
  return [
    'foundry',
    'control-plane',
    'lakes',
    lake,
    'catalogs',
    catalog,
    'replication-groups',
    replicationGroupId,
    'runs',
    ...(params ? [params] : []),
  ] as const;
};

export const getListReplicationRunsQueryOptions = <
  TData = Awaited<ReturnType<typeof listReplicationRuns>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { lake, catalog, replicationGroupId }: ListReplicationRunsPathParameters,
  params?: ListReplicationRunsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listReplicationRuns>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getListReplicationRunsQueryKey({ lake, catalog, replicationGroupId }, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listReplicationRuns>>> = ({ signal }) =>
    listReplicationRuns({ lake, catalog, replicationGroupId }, params, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled:
      lake !== null &&
      lake !== undefined &&
      catalog !== null &&
      catalog !== undefined &&
      replicationGroupId !== null &&
      replicationGroupId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof listReplicationRuns>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type ListReplicationRunsQueryResult = NonNullable<Awaited<ReturnType<typeof listReplicationRuns>>>;
export type ListReplicationRunsQueryError = ErrorType<TLHTTPValidationError>;

export function useListReplicationRuns<
  TData = Awaited<ReturnType<typeof listReplicationRuns>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ListReplicationRunsPathParameters,
  params: undefined | ListReplicationRunsParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof listReplicationRuns>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listReplicationRuns>>,
          TError,
          Awaited<ReturnType<typeof listReplicationRuns>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListReplicationRuns<
  TData = Awaited<ReturnType<typeof listReplicationRuns>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ListReplicationRunsPathParameters,
  params?: ListReplicationRunsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listReplicationRuns>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listReplicationRuns>>,
          TError,
          Awaited<ReturnType<typeof listReplicationRuns>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListReplicationRuns<
  TData = Awaited<ReturnType<typeof listReplicationRuns>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ListReplicationRunsPathParameters,
  params?: ListReplicationRunsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listReplicationRuns>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List Replication Runs
 */

export function useListReplicationRuns<
  TData = Awaited<ReturnType<typeof listReplicationRuns>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { lake, catalog, replicationGroupId }: ListReplicationRunsPathParameters,
  params?: ListReplicationRunsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listReplicationRuns>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListReplicationRunsQueryOptions({ lake, catalog, replicationGroupId }, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary List Replication Runs
 */
export const prefetchListReplicationRunsQuery = async <
  TData = Awaited<ReturnType<typeof listReplicationRuns>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { lake, catalog, replicationGroupId }: ListReplicationRunsPathParameters,
  params?: ListReplicationRunsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listReplicationRuns>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getListReplicationRunsQueryOptions({ lake, catalog, replicationGroupId }, params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary List Replication Runs
 */
export const invalidateListReplicationRuns = async (
  queryClient: QueryClient,
  { lake, catalog, replicationGroupId }: ListReplicationRunsPathParameters,
  params?: ListReplicationRunsParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getListReplicationRunsQueryKey({ lake, catalog, replicationGroupId }, params) },
    options,
  );

  return queryClient;
};

/**
 * @summary Start Replication Run
 */
export const startReplicationRun = (
  { lake, catalog, replicationGroupId }: StartReplicationRunPathParameters,
  tLStartReplicationRunRequest: BodyType<TLStartReplicationRunRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLStartReplicationRunResponse>(
    {
      url: `/foundry/control-plane/lakes/${lake}/catalogs/${catalog}/replication-groups/${replicationGroupId}/runs`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLStartReplicationRunRequest,
      signal,
    },
    options,
  );
};

export const getStartReplicationRunQueryKey = (
  { lake, catalog, replicationGroupId }: StartReplicationRunPathParameters,
  tLStartReplicationRunRequest?: BodyType<TLStartReplicationRunRequest>,
) => {
  return [
    'POST',
    'foundry',
    'control-plane',
    'lakes',
    lake,
    'catalogs',
    catalog,
    'replication-groups',
    replicationGroupId,
    'runs',
    tLStartReplicationRunRequest,
  ] as const;
};

export const getStartReplicationRunQueryOptions = <
  TData = Awaited<ReturnType<typeof startReplicationRun>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { lake, catalog, replicationGroupId }: StartReplicationRunPathParameters,
  tLStartReplicationRunRequest: BodyType<TLStartReplicationRunRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof startReplicationRun>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getStartReplicationRunQueryKey({ lake, catalog, replicationGroupId }, tLStartReplicationRunRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof startReplicationRun>>> = ({ signal }) =>
    startReplicationRun({ lake, catalog, replicationGroupId }, tLStartReplicationRunRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled:
      lake !== null &&
      lake !== undefined &&
      catalog !== null &&
      catalog !== undefined &&
      replicationGroupId !== null &&
      replicationGroupId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof startReplicationRun>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type StartReplicationRunQueryResult = NonNullable<Awaited<ReturnType<typeof startReplicationRun>>>;
export type StartReplicationRunQueryError = ErrorType<TLHTTPValidationError>;

export function useStartReplicationRun<
  TData = Awaited<ReturnType<typeof startReplicationRun>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: StartReplicationRunPathParameters,
  tLStartReplicationRunRequest: BodyType<TLStartReplicationRunRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof startReplicationRun>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof startReplicationRun>>,
          TError,
          Awaited<ReturnType<typeof startReplicationRun>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useStartReplicationRun<
  TData = Awaited<ReturnType<typeof startReplicationRun>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: StartReplicationRunPathParameters,
  tLStartReplicationRunRequest: BodyType<TLStartReplicationRunRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof startReplicationRun>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof startReplicationRun>>,
          TError,
          Awaited<ReturnType<typeof startReplicationRun>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useStartReplicationRun<
  TData = Awaited<ReturnType<typeof startReplicationRun>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: StartReplicationRunPathParameters,
  tLStartReplicationRunRequest: BodyType<TLStartReplicationRunRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof startReplicationRun>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Start Replication Run
 */

export function useStartReplicationRun<
  TData = Awaited<ReturnType<typeof startReplicationRun>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { lake, catalog, replicationGroupId }: StartReplicationRunPathParameters,
  tLStartReplicationRunRequest: BodyType<TLStartReplicationRunRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof startReplicationRun>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getStartReplicationRunQueryOptions(
    { lake, catalog, replicationGroupId },
    tLStartReplicationRunRequest,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Start Replication Run
 */
export const prefetchStartReplicationRunQuery = async <
  TData = Awaited<ReturnType<typeof startReplicationRun>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { lake, catalog, replicationGroupId }: StartReplicationRunPathParameters,
  tLStartReplicationRunRequest: BodyType<TLStartReplicationRunRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof startReplicationRun>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getStartReplicationRunQueryOptions(
    { lake, catalog, replicationGroupId },
    tLStartReplicationRunRequest,
    options,
  );

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Start Replication Run
 */
export const invalidateStartReplicationRun = async (
  queryClient: QueryClient,
  { lake, catalog, replicationGroupId }: StartReplicationRunPathParameters,
  tLStartReplicationRunRequest: BodyType<TLStartReplicationRunRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getStartReplicationRunQueryKey({ lake, catalog, replicationGroupId }, tLStartReplicationRunRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Get Replication Run
 */
export const getReplicationRun = (
  { lake, catalog, replicationGroupId, runId }: GetReplicationRunPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLReplicationGroupRunResponse>(
    {
      url: `/foundry/control-plane/lakes/${lake}/catalogs/${catalog}/replication-groups/${replicationGroupId}/runs/${runId}`,
      method: 'GET',
      signal,
    },
    options,
  );
};

export const getGetReplicationRunQueryKey = ({
  lake,
  catalog,
  replicationGroupId,
  runId,
}: GetReplicationRunPathParameters) => {
  return [
    'foundry',
    'control-plane',
    'lakes',
    lake,
    'catalogs',
    catalog,
    'replication-groups',
    replicationGroupId,
    'runs',
    runId,
  ] as const;
};

export const getGetReplicationRunQueryOptions = <
  TData = Awaited<ReturnType<typeof getReplicationRun>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { lake, catalog, replicationGroupId, runId }: GetReplicationRunPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getReplicationRun>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetReplicationRunQueryKey({ lake, catalog, replicationGroupId, runId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getReplicationRun>>> = ({ signal }) =>
    getReplicationRun({ lake, catalog, replicationGroupId, runId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled:
      lake !== null &&
      lake !== undefined &&
      catalog !== null &&
      catalog !== undefined &&
      replicationGroupId !== null &&
      replicationGroupId !== undefined &&
      runId !== null &&
      runId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getReplicationRun>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetReplicationRunQueryResult = NonNullable<Awaited<ReturnType<typeof getReplicationRun>>>;
export type GetReplicationRunQueryError = ErrorType<TLHTTPValidationError>;

export function useGetReplicationRun<
  TData = Awaited<ReturnType<typeof getReplicationRun>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetReplicationRunPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getReplicationRun>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getReplicationRun>>,
          TError,
          Awaited<ReturnType<typeof getReplicationRun>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetReplicationRun<
  TData = Awaited<ReturnType<typeof getReplicationRun>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetReplicationRunPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getReplicationRun>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getReplicationRun>>,
          TError,
          Awaited<ReturnType<typeof getReplicationRun>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetReplicationRun<
  TData = Awaited<ReturnType<typeof getReplicationRun>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetReplicationRunPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getReplicationRun>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Replication Run
 */

export function useGetReplicationRun<
  TData = Awaited<ReturnType<typeof getReplicationRun>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { lake, catalog, replicationGroupId, runId }: GetReplicationRunPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getReplicationRun>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetReplicationRunQueryOptions({ lake, catalog, replicationGroupId, runId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Replication Run
 */
export const prefetchGetReplicationRunQuery = async <
  TData = Awaited<ReturnType<typeof getReplicationRun>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { lake, catalog, replicationGroupId, runId }: GetReplicationRunPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getReplicationRun>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetReplicationRunQueryOptions({ lake, catalog, replicationGroupId, runId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Replication Run
 */
export const invalidateGetReplicationRun = async (
  queryClient: QueryClient,
  { lake, catalog, replicationGroupId, runId }: GetReplicationRunPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getGetReplicationRunQueryKey({ lake, catalog, replicationGroupId, runId }) },
    options,
  );

  return queryClient;
};

/**
 * @summary List Replication Table Runs
 */
export const listReplicationTableRuns = (
  { lake, catalog, replicationGroupId, runId }: ListReplicationTableRunsPathParameters,
  params?: ListReplicationTableRunsParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLReplicationTableRunPageResponse>(
    {
      url: `/foundry/control-plane/lakes/${lake}/catalogs/${catalog}/replication-groups/${replicationGroupId}/runs/${runId}/table-runs`,
      method: 'GET',
      params,
      signal,
    },
    options,
  );
};

export const getListReplicationTableRunsQueryKey = (
  { lake, catalog, replicationGroupId, runId }: ListReplicationTableRunsPathParameters,
  params?: ListReplicationTableRunsParams,
) => {
  return [
    'foundry',
    'control-plane',
    'lakes',
    lake,
    'catalogs',
    catalog,
    'replication-groups',
    replicationGroupId,
    'runs',
    runId,
    'table-runs',
    ...(params ? [params] : []),
  ] as const;
};

export const getListReplicationTableRunsQueryOptions = <
  TData = Awaited<ReturnType<typeof listReplicationTableRuns>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { lake, catalog, replicationGroupId, runId }: ListReplicationTableRunsPathParameters,
  params?: ListReplicationTableRunsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listReplicationTableRuns>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getListReplicationTableRunsQueryKey({ lake, catalog, replicationGroupId, runId }, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listReplicationTableRuns>>> = ({ signal }) =>
    listReplicationTableRuns({ lake, catalog, replicationGroupId, runId }, params, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled:
      lake !== null &&
      lake !== undefined &&
      catalog !== null &&
      catalog !== undefined &&
      replicationGroupId !== null &&
      replicationGroupId !== undefined &&
      runId !== null &&
      runId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof listReplicationTableRuns>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type ListReplicationTableRunsQueryResult = NonNullable<Awaited<ReturnType<typeof listReplicationTableRuns>>>;
export type ListReplicationTableRunsQueryError = ErrorType<TLHTTPValidationError>;

export function useListReplicationTableRuns<
  TData = Awaited<ReturnType<typeof listReplicationTableRuns>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ListReplicationTableRunsPathParameters,
  params: undefined | ListReplicationTableRunsParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof listReplicationTableRuns>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listReplicationTableRuns>>,
          TError,
          Awaited<ReturnType<typeof listReplicationTableRuns>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListReplicationTableRuns<
  TData = Awaited<ReturnType<typeof listReplicationTableRuns>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ListReplicationTableRunsPathParameters,
  params?: ListReplicationTableRunsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listReplicationTableRuns>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listReplicationTableRuns>>,
          TError,
          Awaited<ReturnType<typeof listReplicationTableRuns>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListReplicationTableRuns<
  TData = Awaited<ReturnType<typeof listReplicationTableRuns>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ListReplicationTableRunsPathParameters,
  params?: ListReplicationTableRunsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listReplicationTableRuns>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List Replication Table Runs
 */

export function useListReplicationTableRuns<
  TData = Awaited<ReturnType<typeof listReplicationTableRuns>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { lake, catalog, replicationGroupId, runId }: ListReplicationTableRunsPathParameters,
  params?: ListReplicationTableRunsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listReplicationTableRuns>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListReplicationTableRunsQueryOptions(
    { lake, catalog, replicationGroupId, runId },
    params,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary List Replication Table Runs
 */
export const prefetchListReplicationTableRunsQuery = async <
  TData = Awaited<ReturnType<typeof listReplicationTableRuns>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { lake, catalog, replicationGroupId, runId }: ListReplicationTableRunsPathParameters,
  params?: ListReplicationTableRunsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listReplicationTableRuns>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getListReplicationTableRunsQueryOptions(
    { lake, catalog, replicationGroupId, runId },
    params,
    options,
  );

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary List Replication Table Runs
 */
export const invalidateListReplicationTableRuns = async (
  queryClient: QueryClient,
  { lake, catalog, replicationGroupId, runId }: ListReplicationTableRunsPathParameters,
  params?: ListReplicationTableRunsParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getListReplicationTableRunsQueryKey({ lake, catalog, replicationGroupId, runId }, params) },
    options,
  );

  return queryClient;
};

/**
 * @summary Get Replication Run By Id
 */
export const getReplicationRunById = (
  { runId }: GetReplicationRunByIdPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLReplicationGroupRunResponse>(
    { url: `/foundry/control-plane/replication-runs/${runId}`, method: 'GET', signal },
    options,
  );
};

export const getGetReplicationRunByIdQueryKey = ({ runId }: GetReplicationRunByIdPathParameters) => {
  return ['foundry', 'control-plane', 'replication-runs', runId] as const;
};

export const getGetReplicationRunByIdQueryOptions = <
  TData = Awaited<ReturnType<typeof getReplicationRunById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { runId }: GetReplicationRunByIdPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getReplicationRunById>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetReplicationRunByIdQueryKey({ runId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getReplicationRunById>>> = ({ signal }) =>
    getReplicationRunById({ runId }, requestOptions, signal);

  return { queryKey, queryFn, enabled: runId !== null && runId !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getReplicationRunById>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetReplicationRunByIdQueryResult = NonNullable<Awaited<ReturnType<typeof getReplicationRunById>>>;
export type GetReplicationRunByIdQueryError = ErrorType<TLHTTPValidationError>;

export function useGetReplicationRunById<
  TData = Awaited<ReturnType<typeof getReplicationRunById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetReplicationRunByIdPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getReplicationRunById>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getReplicationRunById>>,
          TError,
          Awaited<ReturnType<typeof getReplicationRunById>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetReplicationRunById<
  TData = Awaited<ReturnType<typeof getReplicationRunById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetReplicationRunByIdPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getReplicationRunById>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getReplicationRunById>>,
          TError,
          Awaited<ReturnType<typeof getReplicationRunById>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetReplicationRunById<
  TData = Awaited<ReturnType<typeof getReplicationRunById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetReplicationRunByIdPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getReplicationRunById>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Replication Run By Id
 */

export function useGetReplicationRunById<
  TData = Awaited<ReturnType<typeof getReplicationRunById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { runId }: GetReplicationRunByIdPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getReplicationRunById>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetReplicationRunByIdQueryOptions({ runId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Replication Run By Id
 */
export const prefetchGetReplicationRunByIdQuery = async <
  TData = Awaited<ReturnType<typeof getReplicationRunById>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { runId }: GetReplicationRunByIdPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getReplicationRunById>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetReplicationRunByIdQueryOptions({ runId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Replication Run By Id
 */
export const invalidateGetReplicationRunById = async (
  queryClient: QueryClient,
  { runId }: GetReplicationRunByIdPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetReplicationRunByIdQueryKey({ runId }) }, options);

  return queryClient;
};

/**
 * @summary List Sources
 */
export const controlPlaneListSources = (options?: SecondParameter<typeof apiInstance>, signal?: AbortSignal) => {
  return apiInstance<TLListSourcesResponse>({ url: `/foundry/control-plane/sources`, method: 'GET', signal }, options);
};

export const getControlPlaneListSourcesQueryKey = () => {
  return ['foundry', 'control-plane', 'sources'] as const;
};

export const getControlPlaneListSourcesQueryOptions = <
  TData = Awaited<ReturnType<typeof controlPlaneListSources>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof controlPlaneListSources>>, TError, TData>>;
  request?: SecondParameter<typeof apiInstance>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getControlPlaneListSourcesQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof controlPlaneListSources>>> = ({ signal }) =>
    controlPlaneListSources(requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof controlPlaneListSources>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ControlPlaneListSourcesQueryResult = NonNullable<Awaited<ReturnType<typeof controlPlaneListSources>>>;
export type ControlPlaneListSourcesQueryError = ErrorType<unknown>;

export function useControlPlaneListSources<
  TData = Awaited<ReturnType<typeof controlPlaneListSources>>,
  TError = ErrorType<unknown>,
>(
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof controlPlaneListSources>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof controlPlaneListSources>>,
          TError,
          Awaited<ReturnType<typeof controlPlaneListSources>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useControlPlaneListSources<
  TData = Awaited<ReturnType<typeof controlPlaneListSources>>,
  TError = ErrorType<unknown>,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof controlPlaneListSources>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof controlPlaneListSources>>,
          TError,
          Awaited<ReturnType<typeof controlPlaneListSources>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useControlPlaneListSources<
  TData = Awaited<ReturnType<typeof controlPlaneListSources>>,
  TError = ErrorType<unknown>,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof controlPlaneListSources>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List Sources
 */

export function useControlPlaneListSources<
  TData = Awaited<ReturnType<typeof controlPlaneListSources>>,
  TError = ErrorType<unknown>,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof controlPlaneListSources>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getControlPlaneListSourcesQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary List Sources
 */
export const prefetchControlPlaneListSourcesQuery = async <
  TData = Awaited<ReturnType<typeof controlPlaneListSources>>,
  TError = ErrorType<unknown>,
>(
  queryClient: QueryClient,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof controlPlaneListSources>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getControlPlaneListSourcesQueryOptions(options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary List Sources
 */
export const invalidateControlPlaneListSources = async (
  queryClient: QueryClient,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getControlPlaneListSourcesQueryKey() }, options);

  return queryClient;
};

/**
 * @summary Provision Source
 */
export const provisionSource = (
  tLProvisionSourceRequest: BodyType<TLProvisionSourceRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLProvisionSourceResponse>(
    {
      url: `/foundry/control-plane/sources`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLProvisionSourceRequest,
      signal,
    },
    options,
  );
};

export const getProvisionSourceQueryKey = (tLProvisionSourceRequest?: BodyType<TLProvisionSourceRequest>) => {
  return ['POST', 'foundry', 'control-plane', 'sources', tLProvisionSourceRequest] as const;
};

export const getProvisionSourceQueryOptions = <
  TData = Awaited<ReturnType<typeof provisionSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLProvisionSourceRequest: BodyType<TLProvisionSourceRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof provisionSource>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getProvisionSourceQueryKey(tLProvisionSourceRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof provisionSource>>> = ({ signal }) =>
    provisionSource(tLProvisionSourceRequest, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof provisionSource>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ProvisionSourceQueryResult = NonNullable<Awaited<ReturnType<typeof provisionSource>>>;
export type ProvisionSourceQueryError = ErrorType<TLHTTPValidationError>;

export function useProvisionSource<
  TData = Awaited<ReturnType<typeof provisionSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLProvisionSourceRequest: BodyType<TLProvisionSourceRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof provisionSource>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof provisionSource>>,
          TError,
          Awaited<ReturnType<typeof provisionSource>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useProvisionSource<
  TData = Awaited<ReturnType<typeof provisionSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLProvisionSourceRequest: BodyType<TLProvisionSourceRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof provisionSource>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof provisionSource>>,
          TError,
          Awaited<ReturnType<typeof provisionSource>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useProvisionSource<
  TData = Awaited<ReturnType<typeof provisionSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLProvisionSourceRequest: BodyType<TLProvisionSourceRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof provisionSource>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Provision Source
 */

export function useProvisionSource<
  TData = Awaited<ReturnType<typeof provisionSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLProvisionSourceRequest: BodyType<TLProvisionSourceRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof provisionSource>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getProvisionSourceQueryOptions(tLProvisionSourceRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Provision Source
 */
export const prefetchProvisionSourceQuery = async <
  TData = Awaited<ReturnType<typeof provisionSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLProvisionSourceRequest: BodyType<TLProvisionSourceRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof provisionSource>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getProvisionSourceQueryOptions(tLProvisionSourceRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Provision Source
 */
export const invalidateProvisionSource = async (
  queryClient: QueryClient,
  tLProvisionSourceRequest: BodyType<TLProvisionSourceRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getProvisionSourceQueryKey(tLProvisionSourceRequest) }, options);

  return queryClient;
};

/**
 * @summary Update Source
 */
export const updateSource = (
  { sourceName }: UpdateSourcePathParameters,
  tLUpdateSourceRequest: BodyType<TLUpdateSourceRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLUpdateSourceResponse>(
    {
      url: `/foundry/control-plane/sources/${sourceName}`,
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      data: tLUpdateSourceRequest,
      signal,
    },
    options,
  );
};

export const getUpdateSourceQueryKey = (
  { sourceName }: UpdateSourcePathParameters,
  tLUpdateSourceRequest?: BodyType<TLUpdateSourceRequest>,
) => {
  return ['PUT', 'foundry', 'control-plane', 'sources', sourceName, tLUpdateSourceRequest] as const;
};

export const getUpdateSourceQueryOptions = <
  TData = Awaited<ReturnType<typeof updateSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { sourceName }: UpdateSourcePathParameters,
  tLUpdateSourceRequest: BodyType<TLUpdateSourceRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateSource>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getUpdateSourceQueryKey({ sourceName }, tLUpdateSourceRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof updateSource>>> = ({ signal }) =>
    updateSource({ sourceName }, tLUpdateSourceRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: sourceName !== null && sourceName !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof updateSource>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type UpdateSourceQueryResult = NonNullable<Awaited<ReturnType<typeof updateSource>>>;
export type UpdateSourceQueryError = ErrorType<TLHTTPValidationError>;

export function useUpdateSource<
  TData = Awaited<ReturnType<typeof updateSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateSourcePathParameters,
  tLUpdateSourceRequest: BodyType<TLUpdateSourceRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateSource>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof updateSource>>,
          TError,
          Awaited<ReturnType<typeof updateSource>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateSource<
  TData = Awaited<ReturnType<typeof updateSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateSourcePathParameters,
  tLUpdateSourceRequest: BodyType<TLUpdateSourceRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateSource>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof updateSource>>,
          TError,
          Awaited<ReturnType<typeof updateSource>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateSource<
  TData = Awaited<ReturnType<typeof updateSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateSourcePathParameters,
  tLUpdateSourceRequest: BodyType<TLUpdateSourceRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateSource>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Update Source
 */

export function useUpdateSource<
  TData = Awaited<ReturnType<typeof updateSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { sourceName }: UpdateSourcePathParameters,
  tLUpdateSourceRequest: BodyType<TLUpdateSourceRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateSource>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getUpdateSourceQueryOptions({ sourceName }, tLUpdateSourceRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Update Source
 */
export const prefetchUpdateSourceQuery = async <
  TData = Awaited<ReturnType<typeof updateSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { sourceName }: UpdateSourcePathParameters,
  tLUpdateSourceRequest: BodyType<TLUpdateSourceRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateSource>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getUpdateSourceQueryOptions({ sourceName }, tLUpdateSourceRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Update Source
 */
export const invalidateUpdateSource = async (
  queryClient: QueryClient,
  { sourceName }: UpdateSourcePathParameters,
  tLUpdateSourceRequest: BodyType<TLUpdateSourceRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getUpdateSourceQueryKey({ sourceName }, tLUpdateSourceRequest) },
    options,
  );

  return queryClient;
};
